namespace Solovis.Reports.Parameters.Composites {

    export class CompositeSelectionSetEditorDialog {

        private _compositeSetData: CompositeSelectionSetData;
        private _compositeSetDataInitiallySet: boolean;
        private _saveCallback;
        private _saveAsNewCallback;
        private _saveButtonText;

        private _formBuilder: Solovis.UI.InputControls.FormBuilder;
        private _dialog: Solovis.UI.Dialog;

        public constructor(options) {
            this._compositeSetData = options.compositeSetData ? options.compositeSetData : {};
            this._compositeSetDataInitiallySet = !!options.compositeSetData;
            this._saveCallback = options.saveCallback;
            this._saveAsNewCallback = options.saveAsNewCallback;

            this._saveButtonText = options.saveButtonText ? options.saveButtonText : "OK";
        }

        public show() {
            let compositeSetData = this._compositeSetData;

            let formBuilderDiv = $("<div>");
            let formBuilder = new Solovis.UI.InputControls.FormBuilder({
                container: formBuilderDiv,
                width: 830,
                labelColumnWidth: 275
            });
            this._formBuilder = formBuilder;


            let createAssetListBuilder = (container, parameterType: "entity" | "investment", itemTypeLabel: string, initialValue: any) => {
                let itemValues = _.map(initialValue, (id) => {
                    if (parameterType === "entity") {
                        return { entityId: id };
                    }
                    else if (parameterType === "investment") {
                        return { investmentId: id };
                    }
                });

                let assetListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
                    controlHost: container,

                    itemTypeLabel: itemTypeLabel,
                    showEditLinks: false,

                    values: itemValues,

                    editorCreator: (editorDiv, initialValue) => {
                        let assetEditor = new Solovis.UI.InputControls.ParameterInputControl({
                            controlHost: editorDiv,
                            parameterType: parameterType,
                            value: initialValue
                        });
                        assetEditor.setAsCurrentControl();

                        let editorInfo = {
                            getValue: function () {
                                let asset = assetEditor.getValue();

                                if (!Solovis.isNull(asset)) {
                                    if (parameterType === "entity") {
                                        return asset.entityId;
                                    }
                                    else if (parameterType === "investment") {
                                        return asset.investmentId;
                                    }
                                }
                            }
                        };

                        return editorInfo;
                    },

                    newValueGenerator: function () {
                        return null;
                    }
                });
                assetListBuilder.setAsCurrentControl();

                return assetListBuilder;
            };


            formBuilder.addEntry({
                name: "ownerEntityIds",
                label: "Owner Entities",

                initialValue: compositeSetData.ownerEntityIds,

                help: {
                    html: "<p>For each owner entity selected, all investments owned by the designated entity will be"
                        + " included as components in the composite analysis.</p>"
                        + "<p>NOTE: When an Aggregate View entity is the holding on one of the those investments, such"
                        + " investments will always be broken out to show all underlying investments of the aggregate entity.</p>"
                },

                createEditor: (createEditorOptions) => {
                    let initialEntities = _.map(createEditorOptions.initialValue, (entityId) => { return { entityId: entityId }; });

                    let entityListBuilder = createAssetListBuilder(createEditorOptions.container, "entity", "Owner", createEditorOptions.initialValue);

                    let editor = {
                        getValue: () => {
                            return entityListBuilder.getValues();
                        }
                    };

                    return editor;
                }
            });

            formBuilder.addEntry({
                name: "entityIds",
                label: "Entities",

                initialValue: compositeSetData.entityIds,

                help: {
                    html: "<p>Each entity selected will be included as a component in the composite analysis.</p>"
                        + "<p>NOTE: When an Aggregate View entity is selected this way, it will NOT be broken out into child investments.</p>"
                },

                createEditor: (createEditorOptions) => {
                    let initialEntities = _.map(createEditorOptions.initialValue, (entityId) => { return { entityId: entityId }; });

                    let entityListBuilder = createAssetListBuilder(createEditorOptions.container, "entity", "Entity", createEditorOptions.initialValue);

                    let editor = {
                        getValue: () => {
                            return entityListBuilder.getValues();
                        }
                    };

                    return editor;
                }
            });

            formBuilder.addEntry({
                name: "investmentIds",
                label: "Investments",

                initialValue: compositeSetData.investmentIds,

                help: {
                    html: "<p>Each investment selected will be included as a component in the composite analysis.</p>"
                        + "<p>NOTE: When an investment in an Aggregate View entity is selected this way, it will NOT be broken out into child investments.</p>"
                },

                createEditor: (createEditorOptions) => {
                    let initialInvestments = _.map(createEditorOptions.initialValue, (investmentId) => { return { investmentId: investmentId }; });

                    let investmentListBuilder = createAssetListBuilder(createEditorOptions.container, "investment", "Investment", createEditorOptions.initialValue);

                    let editor = {
                        getValue: () => {
                            return investmentListBuilder.getValues();
                        }
                    };

                    return editor;
                }
            });

            formBuilder.addEntry({
                name: "componentSelectionSet",
                label: "Asset Selection Set",
                
                help: {
                    html: "<p>Each member of the specified asset selection set will be included as a component in the composite analysis.</p>"
                        + "<p>NOTE: When an Aggregate View entity or an investment in an Aggregate View entity is a member of the asset selection set, it will NOT be broken out into child investments.</p>"
                },

                initialValue: compositeSetData.componentSelectionSetRef,

                editor: {
                    type: "parameter",
                    parameterOptions: {
                        parameterType: "assetSelectionSet"
                    }
                }
            });

            formBuilder.addEntry({
                name: "useOnlyMostRecentPropertyValue",
                label: "Use Only Most Recent Property Value",

                help: "If enabled, all property values will be evaluated at the overall 'To' date, instead of factoring in changing property values over time to create time slices of components.",

                initialValue: !!compositeSetData.useOnlyMostRecentPropertyValue,

                editor: {
                    type: "bool"
                }
            });

            formBuilder.addEntry({
                name: "groupByCommonHoldings",
                label: "Group By Common Holdings",

                help: "After initial grouping phase is completed, if more than one asset in any sub-group shares a common holding, they will be further grouped together into another sub-group.",

                initialValue: this._compositeSetDataInitiallySet ? !!compositeSetData.groupByCommonHoldings : true,

                editor: {
                    type: "bool"
                }
            });

            formBuilder.addEntry({
                name: "alwaysCreateCommonHoldingGroup",
                label: "Always Create Common Holding Group",

                help: "Create the common holding group even if there is only one line item with the holding.",

                initialValue: this._compositeSetDataInitiallySet ? !!compositeSetData.alwaysCreateCommonHoldingGroup : false,

                editor: {
                    type: "bool"
                },

                isVisible: (formEntry) => {
                    return formEntry.formBuilder.getValueByName("groupByCommonHoldings");
                }
            });

            formBuilder.addEntry({
                name: "removeEmptyComponents",
                label: "Remove Empty Components",

                help: {
                    html: "<p>After the list of candidate components is created, 'empty components' are removed if they match all of the following criteria:</p>"
                        + "<ul><li>No market values at interval from/to dates</li><li>No transactions over requested time period.</li></ul>"
                        + "<p>The model assumes that market values cannot come out of nowhere without a transaction. If the data set contains "
                        + "line items with no market values at interval boundaries and with no transactions but there are market values present intra-interval, it is possible to see different overall returns "
                        + "after enabling 'Remove Empty Components' "
                        + "if those market values that 'come out of nowhere' create returns on periods internal to the overall intervals requested. One workaround is to create a placeholder transaction "
                        + "at the point where values initially appear or when they finally disappear.</p> "
                        + "<p>Ongoing Discussion: There is ongoing discussion of whether we should change 'Remove Empty Components' logic to do its processing after all calculations are done "
                        + "and eliminate rows that don't have values for requested data points. This would cause extra unnecessary processing in environments that meet current data model assumptions. "
                        + "We could consider having multiple modes for 'Remove Empty Components' to accommodate both approaches.</p>"
                },

                initialValue: this._compositeSetDataInitiallySet ? !!compositeSetData.removeEmptyComponents : true,

                editor: {
                    type: "bool"
                }
            });

            formBuilder.addEntry({
                name: "includeZeros",
                label: "Include Zeros",
                initialValue: this._compositeSetDataInitiallySet? !!compositeSetData.includeZeros : false,
                editor: {
                    type: "bool"
                },
                isVisible: (formEntry) => {
                    return formEntry.formBuilder.getValueByName("removeEmptyComponents");
                }
            });

            formBuilder.addEntry({
                name: "createOtherGroups",
                label: "Create Other Groups",

                help: "Any individual components that are located within a sub-group at same level as one or more sub-groups will be grouped into an 'Other' sub-group.",

                initialValue: this._compositeSetDataInitiallySet ? !!compositeSetData.createOtherGroups : true,

                editor: {
                    type: "bool"
                }
            });

            formBuilder.addEntry({
                name: "applyPerformanceOverlays",
                label: "Apply Performance Overlays",

                help: "If fixed performance overlays are configured within the system, then the performance overlays for any components to be included in the composite"
                    + " analysis will also be included (if they have not already been explicitly included).",

                initialValue: this._compositeSetDataInitiallySet ? !!compositeSetData.applyPerformanceOverlays : true,

                editor: {
                    type: "bool"
                }
            });


            formBuilder.addEntry({
                name: "groupingDeclarations",
                label: "Composite Group By",

                help: {
                    html: "<p>The composite groupings determine how the selected components will be aggregated together for the server-side composite analysis, where values"
                        + " and transactions within each composite group will drive the calculations for those line items.</p>"
                        + "<p>NOTE: Server-side compositing is completely separate from the 'Sorting And Grouping' options available within the report table"
                        + " web interface. Also, any 'Filtering' options in the report table web interface will not have any effect on the list of compontents included"
                        + " within the server-side composite analysis.</p>"
                },

                initialValue: compositeSetData.groupingDeclarations,

                createEditor: (createEditorOptions) => {
                    let groupByListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
                        controlHost: createEditorOptions.container,

                        itemTypeLabel: "Composite Grouping",
                        showEditLinks: false,

                        values: createEditorOptions.initialValue,

                        editorCreator: (editorDiv, initialValue) => {
                            let groupingDeclarationControl = new GroupingDeclarationControl({
                                controlHost: editorDiv,
                                grouping: initialValue
                            });
                            groupingDeclarationControl.setAsCurrentControl();

                            let editorInfo = {
                                getValue: function () {
                                    let grouping = groupingDeclarationControl.getGroupingDeclaration();
                                    return grouping;
                                }
                            };

                            return editorInfo;
                        },

                        newValueGenerator: function () {
                            return null;
                        }
                    });
                    groupByListBuilder.setAsCurrentControl();

                    let editor = {
                        getValue: () => {
                            return groupByListBuilder.getValues();
                        }
                    };

                    return editor;
                }
            });


            formBuilder.addEntry({
                name: "lookThrough",
                label: "Look-Through",

                help: {
                    html: "<p>If investment component has holding of type Managed Fund or Drawdown Fund, Look-Through can peer through to underlying holdings.</p>"
                        + "<p>Requirement: Funds must have a cash account.</p>"
                        + "<p>NOTE: Mismatches with non look-through performance can be caused by above/below mismatch on transactions or FX rates on transactions that don't match end of day system FX rates.</p>"
                },

                initialValue: compositeSetData.lookThrough,

                createEditor: (createEditorOptions) => {
                    let lookThroughControl = new CompositeLookThroughControl({
                        controlHost: createEditorOptions.container,
                        lookThrough: createEditorOptions.initialValue
                    });
                    lookThroughControl.setAsCurrentControl();

                    let editor = {
                        getValue: () => {
                            return lookThroughControl.getLookThrough();
                        },

                        setWidth: (newWidth) => {
                            lookThroughControl.setWidth(newWidth);
                        }
                    };

                    return editor;
                }
            });

            this._addTwrSettings();

            formBuilder.addEntry({
                name: "other",
                isGroupHeader: true,
                label: "Other"
            });

            formBuilder.addEntry({
                name: "allLabel",
                label: "'All' Label",

                help: "If report is configured to use composite selection set label instead of 'All', this value will override composite selection set name.",

                initialValue: compositeSetData.allLabel,

                editor: {
                    type: "text"
                }
            });

            formBuilder.setupDone();

            let moreButtons = [];
            if (Solovis.Portal.CachedPermissions.doesUserHavePermission("SelectionSetEdit")) {
                moreButtons.push({
                    label: "Save As New Selection Set",
                    click: () => {
                        this._saveAsNewSelectionSetAndCloseAsync();
                    }
                });
            }

            this._dialog = formBuilder.showAsDialog({
                title: "Edit Composite Selection Set",

                desiredWidth: 830,
                desiredHeight: 700,

                showSaveCancel: true,
                saveButtonText: this._saveButtonText,

                buttons: moreButtons,

                saveCallback: () => {
                    let newOptions = this._getNewCompositeSelectionSetData();

                    this._saveCallback(newOptions);

                    return true;
                }
            });
        }

        public static async editSavedSelectionSet(options: {
            selectionSetId: number;
            saveCallback: (newCompositeData) => void;
            saveAsNewCallback: ({
                compositeSelectionSetId,
                selectionSetData
            }: {
                compositeSelectionSetId: number,
                selectionSetData: any
            }) => void;
        }): Promise<void> {            
            let response = await Solovis.Reports.ReportDataLoader.loadDataSetAsync(
                "/Solovis/Metadata/CompositeSelectionSetList",
                { compositeSelectionSetIds: [options.selectionSetId], includeData: true },
                null,
                {
                    invokeMethod: "invokeAndWait"
                });
                    
            let dataSet = response.result.dataSet;
            let compositeSelectionSetInfo: any = _.find(dataSet.compositeSelectionSets, function (i: any) { return i.id == options.selectionSetId; })
            if (compositeSelectionSetInfo) {
                let compositeSelectionData = compositeSelectionSetInfo.compositeSelectionSetData;

                let editorDialog = new CompositeSelectionSetEditorDialog({
                    compositeSetData: compositeSelectionData,
                    saveCallback: async (newCompositeData) => {
                        await Solovis.Rpc.invokeAndWait(
                            "~/Rpc/CompositeSelectionSetEdit/UpdateSelectionSetData",
                            {
                                selectionSetId: options.selectionSetId,
                                selectionSetData: newCompositeData
                            });

                        if (options.saveCallback) {
                            options.saveCallback(newCompositeData);
                        }
                    },
                    saveButtonText: "Save",

                    saveAsNewCallback: (idAndData) => {
                        if (options.saveAsNewCallback) {
                            options.saveAsNewCallback(idAndData);
                        }
                    }
                });
                editorDialog.show();
            }
            else {
                Solovis.UI.alert("Unable to find asset selection set for id: " + options.selectionSetId);
            }            
        }

        private _addTwrSettings() {
            let compositeSetData = this._compositeSetData ? this._compositeSetData : {};
            let twrSettings = compositeSetData.twrSettings ? compositeSetData.twrSettings : {};

            this._formBuilder.addEntry({
                name: "twrSettingsGroup",
                label: "TWR Settings",
                isGroupHeader: true
            });

            this._formBuilder.addEntry({
                name: "twrSettings.settingsSourceAssetResource",
                label: "TWR Settings Source Asset",

                help: {
                    html: "<p>By default, composite line items will use modified dietz for monthly returns with no intra-period splits."
                        + " The 'TWR Settings Source Asset' allows you to specify an alternate asset from which to pull those settings.</p>"
                        + "<p>NOTE: The 'TWR Settings Source Asset' might typically be the actual portfolio which is the owner entity for the component assets.</p>"
                },

                initialValue: twrSettings.settingsSourceAssetResourceId ? { resourceId: twrSettings.settingsSourceAssetResourceId } : null,

                editor: {
                    type: "parameter",
                    parameterOptions: {
                        parameterType: "entityOrInvestment"
                    }
                }
            });
        }

        private _getNewCompositeSelectionSetData() {
            let newData: CompositeSelectionSetData = {};

            let ownerEntityIds = this._formBuilder.getValueByName("ownerEntityIds");
            if (!_.isEmpty(ownerEntityIds)) {
                newData.ownerEntityIds = ownerEntityIds.filter(function (id) {
                    return id != null;
                });
            }

            let entityIds = this._formBuilder.getValueByName("entityIds");
            if (!_.isEmpty(entityIds)) {
                newData.entityIds = entityIds.filter(function (id) {
                    return id != null;
                });
            }

            let investmentIds = this._formBuilder.getValueByName("investmentIds");
            if (!_.isEmpty(investmentIds)) {
                newData.investmentIds = investmentIds.filter(function (id) {
                    return id != null;
                });
            }

            let componentSelectionSetRef = this._formBuilder.getValueByName("componentSelectionSet");
            if (componentSelectionSetRef) {
                newData.componentSelectionSetRef = componentSelectionSetRef;
            }

            if (this._formBuilder.getValueByName("useOnlyMostRecentPropertyValue")) {
                newData.useOnlyMostRecentPropertyValue = true;
            }

            if (this._formBuilder.getValueByName("groupByCommonHoldings")) {
                newData.groupByCommonHoldings = true;
            }
            if (this._formBuilder.getValueByName("alwaysCreateCommonHoldingGroup")) {
                newData.alwaysCreateCommonHoldingGroup = true;
            }

            if (this._formBuilder.getValueByName("removeEmptyComponents")) {
                newData.removeEmptyComponents = true;
            }
            if (this._formBuilder.getValueByName("includeZeros")) {
                newData.includeZeros = true;
            }
            if (this._formBuilder.getValueByName("createOtherGroups")) {
                newData.createOtherGroups = true;
            }
            if (this._formBuilder.getValueByName("applyPerformanceOverlays")) {
                newData.applyPerformanceOverlays = true;
            }

            let groupingDeclarations = this._formBuilder.getValueByName("groupingDeclarations");
            if (!_.isEmpty(groupingDeclarations)) {
                newData.groupingDeclarations = groupingDeclarations;
            }

            let lookThrough = this._formBuilder.getValueByName("lookThrough");
            if (lookThrough) {
                newData.lookThrough = lookThrough;
            }

            let twrSettingsSourceAssetResource = this._formBuilder.getValueByName("twrSettings.settingsSourceAssetResource");
            if (twrSettingsSourceAssetResource) {
                newData.twrSettings = {
                    settingsSourceAssetResourceId: twrSettingsSourceAssetResource.resourceId
                };
            }

            let allLabel = this._formBuilder.getValueByName("allLabel");
            if (allLabel) {
                newData.allLabel = allLabel;
            }

            return newData;
        }


        private _saveAsNewSelectionSetAndCloseAsync() {
            Solovis.ModuleLoader.modalEnsureModuleListLoaded(["Solovis.ValueEditors"], () => {
                try {
                    let newCompositeSelectionSetData = this._getNewCompositeSelectionSetData();
                    new (Solovis.Site as any).ValueEditors.CompositeSelectionSets.AddCompositeSelectionSetDialog({
                        compositeSelectionSetData: newCompositeSelectionSetData,
                        saveCallback: (newSetData) => {
                            let compositeSelectionSetId = newSetData.compositeSelectionSetId;

                            // We don't call this._saveCallback because that is used by consumer of this class to save the new data.
                            if (this._saveAsNewCallback) {
                                this._saveAsNewCallback({
                                    compositeSelectionSetId: compositeSelectionSetId,
                                    selectionSetData: newCompositeSelectionSetData
                                });
                            }

                            this._dialog.close();
                        }
                    });
                }
                catch (ex) {
                    Solovis.UI.alert("Unable to retrieve configured selection set data.");
                }
            });            
        }
    }

}